import React, { useId, useRef, useState, useMemo, useEffect, useCallback } from 'react';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import moment from 'moment-timezone';

const DatetimeComponent = (Datetime as any)?.default ?? Datetime;

type Mode = 'date' | 'time' | 'datetime' | 'hhmmss';

interface Seg {
  len: number;
  min: number;
  max: number | 'day';
  ph: string;
}

const D: Seg[] = [
  { len: 4, min: 1900, max: 2099, ph: 'YYYY' },
  { len: 2, min: 1, max: 12, ph: 'MM' },
  { len: 2, min: 1, max: 'day', ph: 'DD' },
];
const T: Seg[] = [
  { len: 2, min: 0, max: 23, ph: 'HH' },
  { len: 2, min: 0, max: 59, ph: 'mm' },
  { len: 2, min: 0, max: 59, ph: 'ss' },
];
const RT: Seg[] = [
  { len: 2, min: 0, max: 99, ph: 'HH' },
  { len: 2, min: 0, max: 59, ph: 'mm' },
  { len: 2, min: 0, max: 59, ph: 'ss' },
];
const MODES: Record<Mode, { segs: Seg[]; seps: string[]; fmt: string }> = {
  date: { segs: D, seps: ['-', '-'], fmt: 'YYYY-MM-DD' },
  time: { segs: T, seps: [':', ':'], fmt: 'HH:mm:ss' },
  datetime: {
    segs: [...D, ...T],
    seps: ['-', '-', ' ', ':', ':'],
    fmt: 'YYYY-MM-DD HH:mm:ss',
  },
  hhmmss: { segs: RT, seps: [':', ':'], fmt: 'HH:mm:ss' },
};

function maxDay(vals: string[]): number {
  const y = parseInt(vals[0], 10),
    m = parseInt(vals[1], 10);
  return y && m ? new Date(y, m, 0).getDate() : 31;
}

function resolveMax(seg: Seg, vals: string[]): number {
  return seg.max === 'day' ? maxDay(vals) : seg.max;
}

// Returns the character position of a segment in the display string
function segPos(segs: Seg[], seps: string[], idx: number): number {
  let p = 0;
  for (let i = 0; i < idx; i++) p += segs[i].len + (seps[i]?.length || 0);
  return p;
}

// Builds the masked display string with placeholders for unfilled positions
function buildDisplay(segs: Seg[], seps: string[], vals: string[]): string {
  return segs
    .map((s, i) => {
      const v = vals[i] || '';
      return (i > 0 ? seps[i - 1] : '') + v + s.ph.slice(v.length);
    })
    .join('');
}

// Returns the complete formatted string, or null if any segment is incomplete
function buildOutput(segs: Seg[], seps: string[], vals: string[]): string | null {
  if (segs.some((s, i) => (vals[i] || '').length < s.len)) return null;
  return segs.map((s, i) => (i > 0 ? seps[i - 1] : '') + vals[i].padStart(s.len, '0')).join('');
}

// Extracts digits from a string and distributes them across segments
function parseToSegments(segs: Seg[], value: string): string[] {
  const digits = (value || '').replace(/\D/g, '');
  const result: string[] = [];
  let di = 0;
  for (const s of segs) {
    let v = '';
    for (let j = 0; j < s.len && di < digits.length; j++) v += digits[di++];
    result.push(v);
  }
  return result;
}

// Validates segment values, returns error message or null
function validate(segs: Seg[], vals: string[]): string | null {
  for (let i = 0; i < segs.length; i++) {
    if ((vals[i] || '').length < segs[i].len) continue;
    const n = parseInt(vals[i], 10),
      mx = resolveMax(segs[i], vals);
    if (n < segs[i].min || n > mx) return `Invalid ${segs[i].ph}: must be ${segs[i].min}–${mx}`;
  }
  if (segs.some((s) => s.ph === 'YYYY') && vals[0]?.length === 4 && vals[1]?.length === 2 && vals[2]?.length === 2) {
    const [y, m, d] = vals.slice(0, 3).map((v) => parseInt(v, 10));
    const t = new Date(y, m - 1, d);
    if (t.getFullYear() !== y || t.getMonth() !== m - 1 || t.getDate() !== d) return 'Invalid date (e.g. Feb 31 does not exist)';
  }
  return null;
}

interface MaskedDateTimeFieldProps {
  value?: moment.Moment | string | Date | '';
  onChange?: (value: moment.Moment) => void;
  mode?: Mode;
  showCalendar?: boolean;
  isValidDate?: (d: moment.Moment) => boolean;
  onBlur?: () => void;
  onClose?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** When set, the field is wrapped in a Bootstrap floating label (matching the other Edit* controls). */
  label?: string;
}

const MaskedDateTimeField: React.FC<MaskedDateTimeFieldProps> = ({
  value,
  onChange,
  mode = 'datetime',
  showCalendar = true,
  isValidDate,
  onBlur,
  onClose,
  placeholder,
  disabled,
  className,
  label,
}) => {
  const { segs, seps, fmt } = MODES[mode];
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const justEmittedRef = useRef(false);
  const activeRef = useRef(0);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calendarOriginRef = useRef(false);
  const [calendarKey, setCalendarKey] = useState(0);

  const hasCalendar = showCalendar && mode !== 'hhmmss' && mode !== 'time';

  const externalSegs = useMemo(() => {
    if (!value) return segs.map(() => '');
    const m = moment.isMoment(value) ? value : moment(value);
    return m.isValid() ? parseToSegments(segs, m.format(fmt)) : segs.map(() => '');
  }, [value, segs, fmt]);

  const [vals, setVals] = useState<string[]>(externalSegs);
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    if (justEmittedRef.current) {
      justEmittedRef.current = false;
      return;
    }
    if (buildOutput(segs, seps, vals) !== buildOutput(segs, seps, externalSegs)) setVals(externalSegs);
  }, [externalSegs]);

  const valsFingerprint = vals.join('|');
  useEffect(() => {
    if (calendarOriginRef.current) {
      calendarOriginRef.current = false;
      return;
    }
    setCalendarKey((k) => k + 1);
  }, [valsFingerprint]);

  // Emits onChange with a moment if all segments are complete and valid
  const emitIfComplete = useCallback(
    (v: string[]) => {
      const out = buildOutput(segs, seps, v);
      if (!out || validate(segs, v)) return;
      const m = moment(out, fmt, true);
      if (m.isValid()) {
        justEmittedRef.current = true;
        onChange?.(m);
      }
    },
    [segs, seps, fmt, onChange]
  );

  // Selects the text range of a segment in the input
  const setCursor = useCallback(
    (idx: number) => {
      const el = inputRef.current;
      if (!el) return;
      const s = segPos(segs, seps, idx);
      requestAnimationFrame(() => {
        if (document.activeElement === el) el.setSelectionRange(s, s + segs[idx].len);
      });
    },
    [segs, seps]
  );

  const calendarMoment = useMemo(() => {
    if (!segs.some((s) => s.ph === 'YYYY')) return moment();
    const y = vals[0] || '',
      m = vals[1] || '',
      d = vals[2] || '';
    const hh = vals[3] || '00',
      mm = vals[4] || '00',
      ss = vals[5] || '00';
    if (y.length === 4 && m.length === 2) {
      const dayStr = d.length === 2 ? d : '01';
      const timeStr = `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:${ss.padStart(2, '0')}`;
      const parsed = moment(`${y}-${m}-${dayStr} ${timeStr}`, 'YYYY-MM-DD HH:mm:ss', true);
      if (parsed.isValid()) return parsed;
    }
    return moment();
  }, [vals[0], vals[1], vals[2], vals[3], vals[4], vals[5], segs]);

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setFocused(true);
    if (hasCalendar) setCalendarOpen(true);
    const idx = vals.some((v) => v) ? active : 0;
    setActive(idx);
    setCursor(idx);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      if (wrapperRef.current?.contains(document.activeElement)) {
        inputRef.current?.focus();
        return;
      }
      setFocused(false);
      setCalendarOpen(false);
      const padded = vals.map((v, i) => (v.length > 0 && v.length < segs[i].len ? v.padStart(segs[i].len, '0') : v));
      setVals(padded);
      setError(validate(segs, padded));
      emitIfComplete(padded);
      onBlur?.();
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const cursor = (e.target as HTMLInputElement).selectionStart || 0;
    let idx = 0,
      pos = 0;
    for (let i = 0; i < segs.length; i++) {
      if (cursor <= pos + segs[i].len) {
        idx = i;
        break;
      }
      pos += segs[i].len + (seps[i]?.length || 0);
      idx = i + 1;
    }
    idx = Math.min(idx, segs.length - 1);
    setActive(idx);
    setCursor(idx);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const { key } = e;

    if (/^\d$/.test(key)) {
      e.preventDefault();
      const seg = segs[active];
      const cur = vals[active].length >= seg.len ? '' : vals[active];
      let next = cur + key;
      const nv = [...vals];
      if (seg.len === 2 && next.length === 1 && parseInt(key, 10) * 10 > resolveMax(seg, nv)) next = '0' + key;
      if (next.length === seg.len) {
        const n = parseInt(next, 10),
          mx = resolveMax(seg, nv);
        if (n > mx) next = String(mx).padStart(seg.len, '0');
        if (n < seg.min && seg.ph !== 'YYYY') next = String(seg.min).padStart(seg.len, '0');
      }
      nv[active] = next;
      setVals(nv);
      setError(validate(segs, nv));
      if (nv[active].length >= seg.len && active < segs.length - 1) {
        setActive(active + 1);
        setCursor(active + 1);
      } else setCursor(active);
      emitIfComplete(nv);
      return;
    }

    if (key === 'Backspace') {
      e.preventDefault();
      if (vals[active]) {
        const nv = [...vals];
        nv[active] = vals[active].slice(0, -1);
        setVals(nv);
        setError(validate(segs, nv));
        setCursor(active);
      } else if (active > 0) {
        setActive(active - 1);
        setCursor(active - 1);
      }
      return;
    }

    if (key === 'Delete') {
      e.preventDefault();
      const nv = [...vals];
      nv[active] = '';
      setVals(nv);
      setError(validate(segs, nv));
      setCursor(active);
      return;
    }

    if (key === 'ArrowLeft' && active > 0) {
      e.preventDefault();
      setActive(active - 1);
      setCursor(active - 1);
      return;
    }
    if (key === 'ArrowRight' && active < segs.length - 1) {
      e.preventDefault();
      setActive(active + 1);
      setCursor(active + 1);
      return;
    }

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      e.preventDefault();
      const seg = segs[active],
        nv = [...vals],
        mx = resolveMax(seg, nv);
      if (!vals[active]) {
        nv[active] = String(seg.min).padStart(seg.len, '0');
      } else {
        let n = parseInt(vals[active], 10) + (key === 'ArrowUp' ? 1 : -1);
        if (n > mx) n = seg.min;
        if (n < seg.min) n = mx;
        nv[active] = String(n).padStart(seg.len, '0');
      }
      setVals(nv);
      setCursor(active);
      setError(validate(segs, nv));
      emitIfComplete(nv);
      return;
    }

    if (key === 'Tab') {
      if (!e.shiftKey && active < segs.length - 1) {
        e.preventDefault();
        setActive(active + 1);
        setCursor(active + 1);
        return;
      }
      if (e.shiftKey && active > 0) {
        e.preventDefault();
        setActive(active - 1);
        setCursor(active - 1);
        return;
      }
      setCalendarOpen(false);
      return;
    }

    if (['-', ':', ' ', '/'].includes(key)) {
      e.preventDefault();
      if (active < segs.length - 1) {
        setActive(active + 1);
        setCursor(active + 1);
      }
      return;
    }

    if (key.length === 1 && !e.ctrlKey && !e.metaKey) e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const nv = parseToSegments(segs, e.clipboardData.getData('text'));
    setVals(nv);
    setError(validate(segs, nv));
    emitIfComplete(nv);
  };

  useEffect(() => {
    if (focused && inputRef.current && document.activeElement === inputRef.current) setCursor(activeRef.current);
  }, [vals, focused, setCursor]);

  const displayValue = useMemo(() => {
    if (!focused) return buildOutput(segs, seps, vals) || '';
    return buildDisplay(segs, seps, vals);
  }, [focused, segs, seps, vals]);

  const defaultPh = useMemo(
    () =>
      buildDisplay(
        segs,
        seps,
        segs.map(() => '')
      ),
    [segs, seps]
  );

  const handleBeforeNavigate = useCallback(
    (nextView: string, currentView: string, viewDate: moment.Moment) => {
      const hasDateSegs = segs.some((s) => s.ph === 'YYYY');
      if (!hasDateSegs) return nextView;

      const isMonthSelected = currentView === 'months' && nextView === 'days';
      const isYearSelected = currentView === 'years' && nextView === 'months';

      if ((isMonthSelected || isYearSelected) && moment.isMoment(viewDate) && viewDate.isValid()) {
        calendarOriginRef.current = true;
        const nv = [...vals];
        nv[0] = String(viewDate.year());
        if (isMonthSelected) {
          nv[1] = String(viewDate.month() + 1).padStart(2, '0');
        }
        setVals(nv);
        setError(validate(segs, nv));
        emitIfComplete(nv);
      }

      return nextView;
    },
    [vals, segs, emitIfComplete]
  );

  // Syncs calendar selection back into segment values
  const handleCalendarChange = useCallback(
    (datetime: moment.Moment | string) => {
      if (typeof datetime === 'string' || !datetime.isValid()) return;

      calendarOriginRef.current = true;

      const nv = [...vals];
      const hasDateSegs = segs.some((s) => s.ph === 'YYYY');
      if (hasDateSegs) {
        nv[0] = String(datetime.year());
        nv[1] = String(datetime.month() + 1).padStart(2, '0');
        nv[2] = String(datetime.date()).padStart(2, '0');
      }
      const hasTimeSegs = segs.some((s) => s.ph === 'HH');
      if (hasTimeSegs) {
        const offset = hasDateSegs ? 3 : 0;
        nv[offset] = String(datetime.hour()).padStart(2, '0');
        nv[offset + 1] = String(datetime.minute()).padStart(2, '0');
        nv[offset + 2] = String(datetime.second()).padStart(2, '0');
      }
      setVals(nv);
      setError(validate(segs, nv));
      emitIfComplete(nv);
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    [vals, segs, seps, emitIfComplete]
  );

  const showErr = error && vals.some((v) => v.length > 0);

  return (
    <div className={`${label ? 'form-floating' : ''} ${className ?? ''}`.trim()} ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={label ? inputId : undefined}
        type="text"
        className={`form-control${showErr ? ' is-invalid' : ''}`}
        value={displayValue}
        // With a floating label the placeholder must be blank so the label shows over the empty field; the format mask still
        // appears on focus (via displayValue). Without a label, keep the format mask as the placeholder.
        placeholder={label ? ' ' : placeholder || defaultPh}
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onChange={() => {}}
        autoComplete="off"
        spellCheck={false}
      />
      {label && <label htmlFor={inputId}>{label}</label>}
      {hasCalendar && calendarOpen && (
        <div className="rdtPicker" style={{ display: 'block', position: 'absolute', zIndex: 9999 }} onMouseDown={(e) => e.preventDefault()}>
          <DatetimeComponent
            key={calendarKey}
            initialValue={calendarMoment}
            onChange={handleCalendarChange}
            onBeforeNavigate={handleBeforeNavigate}
            dateFormat="YYYY-MM-DD"
            timeFormat="HH:mm:ss"
            input={false}
            isValidDate={isValidDate}
          />
        </div>
      )}
      {showErr && focused && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default MaskedDateTimeField;
